import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { Icone } from '../Icone';
import { alcanca, moduloPorChave } from '../modulos';
import { useSessao } from '../sessao';
import { Carregando, ErroDaApi } from '../ui';
import { IndicadoresDoPeriodo, PERIODOS } from '../painel/IndicadoresDoPeriodo';
import { PainelDoEspaco } from '../painel/PainelDoEspaco';
import { PilhaDePendencias } from '../painel/PilhaDePendencias';
import { ResumoDoPainel } from '../painel/ResumoDoPainel';
import {
  COMPLEMENTO_DO_DIA_TRANQUILO, indicadoresDoLaboratorio, pendenciasDoLaboratorio,
  type DadosDoPainel, type Semana,
} from '../painel/doLaboratorio';
import { comNumero, type GrupoDoResumo } from '../painel/tipos';
import { usePainelVivo } from '../painel/usePainelVivo';
import { gravarComigo, gravarVisto, lerComigo, lerVisto, subiuDesde } from '../painel/vistoNoAparelho';
import {
  ChegadasPrevistas, ConclusaoAoLaudo, EquipamentosDoPainel, OcupacaoResumo, RodapeDoPeriodo,
  TempoDeEntrega, type DadosLab,
} from './PainelLaboratorio';
/* O que é do LabManager no desenho (o botão e a régua de «Gráficos e
   máquinas»): a folha dele, e não a do molde. */
import '../painel/doLaboratorio.css';

/**
 * O Painel do LabManager — onda 1, refeito no molde na onda 318 (spec 06b).
 *
 * A tela responde **uma** pergunta: *o que precisa de mim hoje?* Não é
 * "quantos ensaios o laboratório fez no mês" — isso é relatório, e
 * relatório se lê uma vez por mês. Painel que responde a pergunta errada
 * vira decoração no segundo dia, e aí ninguém olha mais nem quando ele
 * tem razão.
 *
 * ── O molde ───────────────────────────────────────────────────────────
 *
 * «Um painel por área» (Humberto, 08/10/2026): este é o do LabManager, e é
 * o primeiro montado com as peças de `web/src/painel/`. A tela só busca os
 * dados e entrega ao molde o que o adaptador (`doLaboratorio.ts`) traduz:
 *
 *  · «Hoje» — o resumo de quatro números e a pilha de cartões, de
 *    `GET /painel` (tabela 5.1 da spec);
 *  · «Andamento» — os quatro indicadores do período, de
 *    `GET /painel/laboratorio` (tabela 5.2). É pedido logo, junto com o
 *    Hoje, porque no PC os dois aparecem lado a lado;
 *  · «Gráficos e máquinas» — o botão no pé do Andamento abre, por baixo, os
 *    gráficos e as máquinas de `PainelLaboratorio.tsx`. Só são montados
 *    quando abertos: no celular a aba Andamento fica escondida até o toque,
 *    e nada é medido escondido.
 *
 * ── Três regras que valem para todo número desta tela ─────────────────
 *
 * **Nenhum número sem o que fazer com ele.** Cada cartão diz a
 * consequência e leva, com um toque, à tela onde se resolve. O porquê,
 * que antes ocupava o meio da tela, está no «i» de cada cartão.
 *
 * **Cartão vazio some.** Um Painel cheio de zeros ensina a não olhar. O
 * resumo é a exceção, e de propósito: zero no resumo é notícia boa, e
 * aparece em cinza.
 *
 * **Nenhum limiar inventado.** Quantos dias parados tornam um ensaio
 * atrasado é decisão da Testesolo, e está aberta (`PD-75`). A cor do
 * cartão vem do TIPO da situação, nunca dos dias; o cartão diz **há quanto
 * tempo** o mais parado espera e deixa o gestor julgar.
 *
 * ── As três melhorias da spec 5.3 ─────────────────────────────────────
 *
 *  · M1 · «Tudo | Comigo»: em «Comigo» ficam os cartões cuja permissão
 *    (a última coluna da tabela 5.1) a pessoa tem. A escolha volta na
 *    próxima visita — fica no aparelho, por usuário (`vistoNoAparelho`).
 *  · M2 · «+N desde a sua última visita»: ao abrir, os números da visita
 *    anterior são lidos UMA vez — é a base desta visita, e a atualização
 *    automática não a muda. Os números que a tela mostra são gravados a
 *    cada resposta: a próxima visita compara com o que a pessoa viu por
 *    último.
 *  · M3 · o Hoje e o Andamento se pedem de novo a cada 5 minutos e ao
 *    voltar para a aba (`usePainelVivo`); a cabeça diz «atualizado às».
 *  · M4 · a tendência: as 8 semanas de `GET /painel/laboratorio/semanas`
 *    viram o minigráfico embaixo de três números do Andamento. Ela não
 *    depende do período escolhido, e é enfeite do número, não o número:
 *    se falhar, os indicadores aparecem sem o minigráfico.
 *
 * O aparelho que não guarda nada (janela privada) não quebra a tela: abre
 * em «Tudo», nada aparece como novo, e o «Comigo» vale na visita.
 *
 * Tudo isso é de UMA pessoa: o «voltar a ser eu» do «Entrar como» troca a
 * pessoa sem sair da tela, e o painel se refaz inteiro para a de agora.
 *
 * ── Quem não tem o Painel ─────────────────────────────────────────────
 *
 * Quem não tem `indicador.ver` (o laboratorista, que trabalha pela Fila)
 * não lê mais um erro de permissão: vê para onde ir (spec 06b §5.4). E o
 * Painel nem é pedido à API — um 403 no console é ruído para quem depura.
 */

/** O período ABRE sempre com 30 dias (Humberto, 02/10: «Abra por padrão
    com 30 dias»). A aba e o período ficam só no endereço — recarregar a
    página ou mandar o link mantém; entrar de novo pelo menu volta ao
    padrão. Por isso não se guardam no navegador. */
const PERIODO_PADRAO = 30;

/** AAAA-MM-DD do dia LOCAL — `toISOString` daria o dia de Greenwich. */
function isoLocal(dt: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
}

/**
 * Os endereços das abas antigas (`?aba=agora|geral|equipamentos`, guardados
 * em favorito ou mandados por e-mail) caem na parte nova que fala daquilo:
 * «Agora» é o Hoje; a «Visão geral» e as «Equipamentos» são o Andamento — e
 * as máquinas abrem os gráficos.
 */
const ABA_ANTIGA: Record<string, 'hoje' | 'andamento'> = {
  agora: 'hoje', geral: 'andamento', equipamentos: 'andamento',
};

const GRAFICOS = 'Gráficos e máquinas';

/** O espaço deste painel: as chaves do aparelho (M1, M2) são as dele. */
const ESPACO = 'labmanager';

/** A tendência só muda de um dia para o outro: pedi-la a cada 30 minutos basta. */
const TRINTA_MINUTOS = 30 * 60_000;

export function Painel() {
  const { pode, eu } = useSessao();
  if (!pode('indicador.ver')) return <SemPainel />;
  /* «Voltar a ser eu» troca o usuário sem sair da tela. Sem a chave, o
     painel montado continuaria o da persona: o «Comigo» e a base do «novo»
     dela, os números que ela recebeu — e gravados na chave de quem voltou.
     Com a chave, outra pessoa é outro painel: lê as escolhas e a base dela
     e pede tudo de novo, com o token dela. */
  return <PainelDoLaboratorio key={eu?.usuarioId} />;
}

/** Quem não tem o Painel (spec 06b §5.4): uma frase e o caminho. */
function SemPainel() {
  const { eu } = useSessao();
  const fila = moduloPorChave('fila');
  const temFila = !!fila && alcanca(fila, eu?.permissoes ?? []);
  return (
    <section className="pn-sem-painel" aria-labelledby="pn-sem-painel-titulo">
      <h1 id="pn-sem-painel-titulo">O Painel é para quem gerencia.</h1>
      {temFila && <p>O que é seu para hoje está na Fila do laboratório.</p>}
      {temFila
        ? <Link to="/fila" className="botao pn-sem-painel-acao">Abrir a Fila</Link>
        : <Link to="/" className="botao pn-sem-painel-acao">Ir para o início</Link>}
    </section>
  );
}

/** O Hoje: `GET /painel`, o que precisa de alguém agora. */
const buscarHoje = () => api<DadosDoPainel>('/painel');

/** M4: as 8 semanas do minigráfico (não dependem do período escolhido). */
const buscarSemanas = () => api<{ semanas: Semana[] }>('/painel/laboratorio/semanas');

function PainelDoLaboratorio() {
  const { pode, eu } = useSessao();
  const usuario = eu?.usuarioId ?? '';
  const [sp, setSp] = useSearchParams();
  const diasPedido = Number(sp.get('dias'));
  const dias = (PERIODOS as readonly number[]).includes(diasPedido) ? diasPedido : PERIODO_PADRAO;
  const abaAntiga = ABA_ANTIGA[sp.get('aba') ?? ''];
  const pediuMaquinas = sp.get('aba') === 'equipamentos';

  /* M3: as duas metades se pedem de novo sozinhas. O Andamento muda de
     pergunta quando muda o período. */
  const buscarAndamento = useCallback(() => {
    const de = new Date();
    de.setDate(de.getDate() - (dias - 1));
    return api<DadosLab>(`/painel/laboratorio?de=${isoLocal(de)}`);
  }, [dias]);
  const { dados: d, erro, atualizadoEm } = usePainelVivo(buscarHoje);
  const { dados: lab, erro: erroLab } = usePainelVivo(buscarAndamento);
  /* M4: o erro da tendência não se mostra — sem ela, o número vai sem o
     minigráfico, e o Andamento continua. */
  const { dados: tendencia } = usePainelVivo(buscarSemanas, TRINTA_MINUTOS);

  const [filtro, setFiltro] = useState<GrupoDoResumo | null>(null);
  /* M1: a escolha da visita anterior (no aparelho, por usuário). */
  const [comigo, setComigo] = useState(() => lerComigo(usuario, ESPACO));
  const escolherComigo = useCallback((v: boolean) => {
    setComigo(v);
    gravarComigo(usuario, v, ESPACO);
  }, [usuario]);
  /* M2: a base é a da visita anterior, lida uma vez ao abrir. */
  const [anterior] = useState(() => lerVisto(usuario, ESPACO));
  const [graficos, setGraficos] = useState(pediuMaquinas);
  const rolarAosGraficos = useRef(pediuMaquinas);

  /* `?aba=agora|geral|equipamentos` → a parte nova (e as máquinas abertas). */
  useEffect(() => {
    if (!abaAntiga) return;
    const n = new URLSearchParams(sp);
    n.set('aba', abaAntiga);
    setSp(n, { replace: true });
  }, [abaAntiga, sp, setSp]);

  /* Quem abre os gráficos vai até eles: no PC eles aparecem embaixo das
     duas colunas, fora da vista de quem clicou no pé do Andamento. Pelo
     link antigo (`?aba=equipamentos`) eles abrem sozinhos — e a rolagem
     espera o Hoje também: a pilha que chegasse depois cresceria a coluna
     ao lado e empurraria os gráficos para baixo do lugar rolado. */
  const hojeChegou = !!d || !!erro;
  useEffect(() => {
    if (!graficos || !lab || !hojeChegou || !rolarAosGraficos.current) return;
    rolarAosGraficos.current = false;
    document.getElementById('pn-graficos')?.scrollIntoView({ block: 'start' });
  }, [graficos, lab, hojeChegou]);

  function mudarDias(n: number) {
    const q = new URLSearchParams(sp);
    q.set('dias', String(n));
    setSp(q, { replace: true });
  }

  const pendencias = useMemo(() => (d ? pendenciasDoLaboratorio(d) : []), [d]);
  const indicadores = useMemo(
    () => (lab ? indicadoresDoLaboratorio(lab, tendencia?.semanas) : []), [lab, tendencia]);

  /* M2: o número de cada cartão que a tela mostra agora… */
  const numeros = useMemo(
    () => Object.fromEntries(comNumero(pendencias).map((p) => [p.chave, Number(p.numero)])),
    [pendencias]);
  /* …comparado com a base desta visita; e gravado para a próxima. */
  const novos = useMemo(() => subiuDesde(anterior, numeros), [anterior, numeros]);
  useEffect(() => {
    if (d) gravarVisto(usuario, numeros, ESPACO);
  }, [d, numeros, usuario]);

  const hoje = d ? (
    <>
      <ResumoDoPainel pendencias={pendencias} filtro={filtro} aoFiltrar={setFiltro} />
      <PilhaDePendencias
        pendencias={pendencias}
        filtroDoResumo={filtro}
        comigo={comigo}
        aoComigo={escolherComigo}
        pode={pode}
        novos={novos}
        complementoDoTranquilo={COMPLEMENTO_DO_DIA_TRANQUILO}
      />
    </>
  ) : erro ? <ErroDaApi erro={erro} /> : <Carregando texto="Montando o painel" />;

  /* O Andamento fica montado enquanto os números chegam (ao abrir e ao
     trocar o período): o foco de quem tocou «90 dias» não cai no corpo da
     página. Sem número nenhum e com erro, o erro vai no lugar dos números. */
  const andamento = (
    <>
      <IndicadoresDoPeriodo indicadores={indicadores} dias={dias} aoDias={mudarDias}
        carregando={!lab && !erroLab} aviso={!lab && erroLab ? <ErroDaApi erro={erroLab} /> : undefined} />
      {lab && (
        <button
          type="button"
          className="botao secundario pn-graficos"
          aria-expanded={graficos}
          aria-controls={graficos ? 'pn-graficos' : undefined}
          onClick={() => {
            rolarAosGraficos.current = !graficos;
            setGraficos(!graficos);
          }}
        >
          {GRAFICOS}
          <Icone nome="direita" />
        </button>
      )}
    </>
  );

  /* As peças de `PainelLaboratorio.tsx` (classes `pl-`) desenhadas na régua
     de letras e de alvos do Painel novo: o `pn-graficos-e-maquinas` em
     volta delas é o escopo das regras do `painel.css` (Ruling R22). */
  const abaixo = graficos && lab ? (
    <section id="pn-graficos" className="pn-abaixo" aria-labelledby="pn-graficos-titulo">
      <h2 id="pn-graficos-titulo" className="pn-abaixo-titulo">{GRAFICOS}</h2>
      <div className="painel-lab pn-graficos-e-maquinas">
        <TempoDeEntrega d={lab} />
        <div className="pl-grade duas">
          <ConclusaoAoLaudo d={lab} />
          <ChegadasPrevistas d={lab} />
        </div>
        <OcupacaoResumo d={lab}
          aoVerMaquinas={() => document.getElementById('pl-t-calor')?.scrollIntoView({ block: 'start' })} />
        <EquipamentosDoPainel d={lab} />
        <RodapeDoPeriodo d={lab} />
      </div>
    </section>
  ) : null;

  return <PainelDoEspaco hoje={hoje} andamento={andamento} atualizadoEm={atualizadoEm} abaixo={abaixo} />;
}
